/**
 * Advisor Context API Service
 *
 * This service provides methods for interacting with the zenith-service Advisor Context API.
 * It enables Atlas to share chart screenshots, metadata, and prompts with Advisor.
 *
 * Authentication:
 * - Uses Cognito idToken from the current session
 * - Token is passed as Bearer authentication to zenith-service
 * - zenith-service decodes the JWT to extract user info (email, user_id)
 *
 * Usage:
 *   import { advisorContextService, openAdvisorWithContext } from 'src/services/advisorContext';
 *
 *   // Create context and open Advisor
 *   await openAdvisorWithContext({
 *     pageUrl: window.location.href,
 *     prompt: 'Analyze this chart',
 *     filterMetadata: { category: 'Electronics' },
 *     screenshot: chartImageBlob,
 *   });
 */

import { safelyFetchAuthSession } from 'src/utils/auth';
import { getAdvisorHost, getAppStage } from 'src/utils/app';

// Types — aligned with backend AttachmentInput (advisor_context.py)
export interface AttachmentInfo {
  type: 'screenshot' | 'csv' | 'json';
  key: string;
  filename: string;
  mimeType: string;
  url: string;
}

export interface CreateContextRequest {
  /** Optional pre-generated context ID (from upload-url response).
   *  When provided, the DynamoDB record uses this ID so it matches the S3 key. */
  contextId?: string;
  source: 'atlas' | 'beacon' | 'advertising';
  pageUrl: string;
  /**
   * User email from Atlas/Beacon session (e.g., 'ttran-king@stackline.com').
   * This is the company-specific email, not the Cognito email which may be shared across companies.
   * Used for ownership validation when retrieving or deleting contexts.
   */
  userEmail: string;
  description?: string;
  filterMetadata?: Record<string, unknown>;
  appMetadata?: Record<string, unknown>;
  attachments?: AttachmentInfo[];
  batchId?: string;
}

export interface CreateContextResponse {
  contextId: string;
  source: string;
  pageUrl: string;
  createdAt: string;
  expiresAt: string;
  attachmentCount: number;
}

export interface UploadUrlRequest {
  filename: string;
  mimeType: string;
  source: string;
}

// Aligned with backend UploadUrlResponse (advisor_context.py)
export interface UploadUrlResponse {
  /** Pre-generated context ID embedded in the S3 key.
   *  Pass this to createContext so the DynamoDB record uses the same ID. */
  contextId: string;
  uploadUrl: string;
  attachmentKey: string;
  viewableUrl: string;
  expiresAt: number;
}

export interface ContextData {
  contextId: string;
  source: string;
  pageUrl: string;
  description?: string;
  filterMetadata?: Record<string, unknown>;
  appMetadata?: Record<string, unknown>;
  attachments?: AttachmentInfo[];
  createdAt: string;
  expiresAt: string;
}

// Configuration
const getAdvisorServiceUrl = (): string => {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost')) {
    return 'http://localhost:8001';
  }
  if (getAppStage() === 'prod') {
    return 'https://advisor.stackline.com';
  }
  return 'https://advisor-dev.stackline.com';
};

/**
 * Resolved filter IDs ready for Advisor (URL params or filterMetadata).
 */
export interface AdvisorFilterIds {
  retailerIds: number[];
  categoryIds: number[];
  subcategoryIds: number[];
  segments: string[];
}

/**
 * Extract and merge filter IDs from the current page context.
 *
 * Combines:
 * 1. mainEntity-derived IDs (the entity being viewed, e.g. category page -> categoryId)
 * 2. Atlas filter modal selections from Redux `state.filters`
 *
 * Deduplicates with Set so overlapping IDs are not sent twice.
 *
 * @param retailer - Redux `state.retailer`
 * @param mainEntity - Redux `state.entityService.mainEntity`
 * @param filters - Redux `state.filters` (Atlas filter modal selections)
 */
export const extractAdvisorFilterIds = (
  retailer: { id?: string | number } | null | undefined,
  mainEntity: { id: string | number; type: string; parentCategoryId?: string | number; categoryIds?: (string | number)[] } | null | undefined,
  filters: { category?: any[] | null; subcategory?: any[] | null; segment?: { id: string | number } | null }
): AdvisorFilterIds => {
  const retailerIds: number[] = retailer?.id ? [Number(retailer.id)] : [];
  let categoryIds: number[] = [];
  let subcategoryIds: number[] = [];
  let segments: string[] = [];

  // Derive IDs from the main entity (page-level context)
  if (mainEntity) {
    switch (mainEntity.type) {
      case 'category':
        categoryIds = [Number(mainEntity.id)];
        break;
      case 'subcategory':
        subcategoryIds = [Number(mainEntity.id)];
        if (mainEntity.parentCategoryId) {
          categoryIds = [Number(mainEntity.parentCategoryId)];
        } else if (mainEntity.categoryIds?.length) {
          categoryIds = mainEntity.categoryIds.map(Number);
        }
        break;
      case 'segment':
        segments = [String(mainEntity.id)];
        break;
      default:
        break;
    }
  }

  // Merge Atlas filter modal selections on top of mainEntity-derived IDs
  if (filters.category?.length) {
    const filterCatIds = filters.category.map((c: any) => Number(c.id));
    categoryIds = [...new Set([...categoryIds, ...filterCatIds])];
  }
  if (filters.subcategory?.length) {
    const filterSubIds = filters.subcategory.map((sc: any) => Number(sc.id));
    subcategoryIds = [...new Set([...subcategoryIds, ...filterSubIds])];
    const parentCatIds = filters.subcategory
      .map((sc: any) => sc.parentCategoryId)
      .filter(Boolean)
      .map(Number);
    categoryIds = [...new Set([...categoryIds, ...parentCatIds])];
  }
  if (filters.segment && !segments.length) {
    segments = [String(filters.segment.id)];
  }

  return { retailerIds, categoryIds, subcategoryIds, segments };
};

/**
 * Build Advisor URL with optional parameters.
 *
 * Supports:
 * - contextId: Chart context from Atlas/Beacon (backend API flow)
 * - prompt: Pre-fill chat input text
 * - retailers/categories/subcategories/segments: Filter IDs for pre-populating
 *   the Advisor filter modal (lightweight URL param flow, no API call)
 */
export const buildAdvisorUrl = (params?: {
  contextId?: string;
  prompt?: string;
  retailers?: number[];
  categories?: number[];
  subcategories?: number[];
  segments?: string[];
}): string => {
  const advisorHost = getAdvisorHost();
  const searchParams = new URLSearchParams();

  if (params?.contextId) {
    searchParams.set('contextId', params.contextId);
  }
  if (params?.prompt) {
    searchParams.set('prompt', params.prompt);
  }
  // Filter IDs as comma-separated values
  if (params?.retailers?.length) {
    searchParams.set('retailers', params.retailers.join(','));
  }
  if (params?.categories?.length) {
    searchParams.set('categories', params.categories.join(','));
  }
  if (params?.subcategories?.length) {
    searchParams.set('subcategories', params.subcategories.join(','));
  }
  if (params?.segments?.length) {
    searchParams.set('segments', params.segments.join(','));
  }

  const queryString = searchParams.toString();
  return queryString ? `https://${advisorHost}/?${queryString}` : `https://${advisorHost}/`;
};

/**
 * Get Bearer token from current Cognito session
 */
const getAuthHeader = async (): Promise<Record<string, string>> => {
  try {
    const session = await safelyFetchAuthSession();
    if (session?.tokens?.idToken) {
      const token = session.tokens.idToken.toString();
      return { Authorization: `Bearer ${token}` };
    }
  } catch (error) {
    console.error('Failed to get auth session for Advisor API:', error);
  }
  return {};
};

/**
 * Advisor Context API client
 */
export const advisorContextService = {
  /**
   * Get a presigned URL for direct S3 upload
   */
  async getUploadUrl(
    filename: string,
    mimeType: string,
    source: string = 'atlas'
  ): Promise<UploadUrlResponse> {
    const baseUrl = getAdvisorServiceUrl();
    const response = await fetch(`${baseUrl}/api/v3/advisor-context/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
      },
      body: JSON.stringify({ filename, mimeType, source }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get upload URL: ${response.status} ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Upload a file directly to S3 using presigned URL
   */
  async uploadFile(uploadUrl: string, file: Blob, mimeType: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': mimeType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file: ${response.status} ${response.statusText}`);
    }
  },

  /**
   * Create a context in DynamoDB
   */
  async createContext(request: CreateContextRequest): Promise<CreateContextResponse> {
    const baseUrl = getAdvisorServiceUrl();
    const response = await fetch(`${baseUrl}/api/v3/advisor-context/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to create context: ${response.status} ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get a context by ID
   * @param contextId - The context ID to retrieve
   * @param userEmail - Atlas/Beacon session email for ownership validation
   */
  async getContext(contextId: string, userEmail: string): Promise<ContextData> {
    const baseUrl = getAdvisorServiceUrl();
    const params = new URLSearchParams({ userEmail });
    const response = await fetch(`${baseUrl}/api/v3/advisor-context/${contextId}?${params}`, {
      method: 'GET',
      headers: {
        ...(await getAuthHeader()),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get context: ${response.status} ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Delete a context
   * @param contextId - The context ID to delete
   * @param userEmail - Atlas/Beacon session email for ownership validation
   */
  async deleteContext(contextId: string, userEmail: string): Promise<void> {
    const baseUrl = getAdvisorServiceUrl();
    const params = new URLSearchParams({ userEmail });
    const response = await fetch(`${baseUrl}/api/v3/advisor-context/${contextId}?${params}`, {
      method: 'DELETE',
      headers: {
        ...(await getAuthHeader()),
      },
    });

    if (response.status !== 204 && !response.ok) {
      throw new Error(`Failed to delete context: ${response.status} ${response.statusText}`);
    }
  },
};

/**
 * Options for opening Advisor with context
 */
export interface OpenAdvisorOptions {
  pageUrl: string;
  /**
   * User email from Atlas/Beacon session (e.g., 'ttran-king@stackline.com').
   * Required for ownership validation.
   */
  userEmail: string;
  description?: string;
  filterMetadata?: Record<string, unknown>;
  appMetadata?: Record<string, unknown>;
  screenshot?: Blob;
  screenshotFilename?: string;
  source?: 'atlas' | 'beacon' | 'advertising';
}

/**
 * Create context (with optional screenshot) and open Advisor in a new tab
 *
 * @param options - Context options including pageUrl, userEmail, description, metadata, and optional screenshot
 * @returns The contextId if successful
 */
export const openAdvisorWithContext = async (options: OpenAdvisorOptions): Promise<string> => {
  const {
    pageUrl,
    userEmail,
    description,
    filterMetadata,
    appMetadata,
    screenshot,
    screenshotFilename = 'chart-screenshot.webp',
    source = 'atlas',
  } = options;

  const attachments: AttachmentInfo[] = [];
  let uploadContextId: string | undefined;

  // Upload screenshot if provided
  if (screenshot) {
    try {
      const mimeType = screenshot.type || 'image/webp';

      // Get presigned URL (returns contextId, S3 key + CloudFront viewable URL)
      const { contextId: uploadCtxId, uploadUrl, attachmentKey, viewableUrl } =
        await advisorContextService.getUploadUrl(screenshotFilename, mimeType, source);

      uploadContextId = uploadCtxId;

      // Upload to S3
      await advisorContextService.uploadFile(uploadUrl, screenshot, mimeType);

      // Add to attachments — both key (S3) and url (CloudFront) are stored in DynamoDB
      attachments.push({
        type: 'screenshot',
        key: attachmentKey,
        filename: screenshotFilename,
        mimeType,
        url: viewableUrl,
      });
    } catch (error) {
      console.error('Failed to upload screenshot:', error);
      // Continue without screenshot - context can still be created
    }
  }

  // Create context — pass uploadContextId so DynamoDB record matches S3 key
  const contextResponse = await advisorContextService.createContext({
    ...(uploadContextId ? { contextId: uploadContextId } : {}),
    source,
    pageUrl,
    userEmail,
    description,
    filterMetadata,
    appMetadata,
    attachments: attachments.length > 0 ? attachments : undefined,
  });

  // Open Advisor with contextId
  const advisorUrl = buildAdvisorUrl({ contextId: contextResponse.contextId });
  window.open(advisorUrl, '_blank', 'noopener,noreferrer');

  return contextResponse.contextId;
};

/**
 * Simple redirect to Advisor with just a prompt (no context API call)
 * Used for basic text prompts from the search bar
 */
export const openAdvisorWithPrompt = (prompt: string): void => {
  const url = buildAdvisorUrl({ prompt: prompt.trim() || undefined });
  window.open(url, '_blank', 'noopener,noreferrer');
};

export default advisorContextService;
